import React, { useState, useCallback, useRef, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { io } from 'socket.io-client';
import DashboardContext from './DashboardContext';
import { dashboardService } from '../../services/dashboard/dashboardService';
import { voucherService } from '../../services/vouchers/voucherService';
import { appBookingService } from '../../services/appBookings/appBookingService';
import { agentService } from '../../services/agent/agentService';
import { cleanAndParseSummary, postProcessAiData, mergeVoucherData } from '../../utils/voucherHelpers';
import dummyVouchers from './dummyVouchers';

const SOCKET_SERVER_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:8000';

const DashboardState = (props) => {
    // Dashboard Stats & Data
    const [stats, setStats] = useState(null);
    const [recentVouchers, setRecentVouchers] = useState([]);
    const [upcomingVouchers, setUpcomingVouchers] = useState([]);
    const [recentForex, setRecentForex] = useState([]);
    const [recentReviews, setRecentReviews] = useState([]);
    const [agentProfile, setAgentProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [agentUpdatesEnabled, setAgentUpdatesEnabled] = useState(true);
    const socketRef = useRef(null);

    // AI Analysis State
    const [analysisState, setAnalysisState] = useState({
        isAnalyzing: false,
        progress: 0,
        result: null,
        isMinimized: false,
        error: null,
        files: [],
        analysisType: null // 'initial' or 'enrich'
    });

    const [allLocations, setAllLocations] = useState([]);
    const [activeTab, setActiveTabState] = useState('home');

    const setActiveTab = useCallback((tabId) => {
        setActiveTabState(tabId);
        window.scrollTo(0, 0);
    }, []);

    const fetchDashboardData = useCallback(async () => {
        setLoading(true);
        try {
            const [statsResponse, vouchersResponse, bookingsResponse, profileResponse] = await Promise.all([
                dashboardService.getStats(),
                voucherService.getVouchers().catch(() => []),
                appBookingService.getAppBookings().catch(() => []),
                agentService.getProfile().catch(() => null)
            ]);

            if (statsResponse?.success) {
                const statsData = statsResponse.data?.stats || {};

                // OVERRIDE totalVouchers with the actual count from the vouchers list
                const vouchersList = Array.isArray(vouchersResponse)
                    ? vouchersResponse
                    : (Array.isArray(vouchersResponse?.vouchers) ? vouchersResponse.vouchers : (Array.isArray(vouchersResponse?.data) ? vouchersResponse.data : []));
                statsData.totalVouchers = vouchersList.length;

                // OVERRIDE totalBookings with the actual count from the bookings list
                const bookingsList = Array.isArray(bookingsResponse?.data)
                    ? bookingsResponse.data
                    : (Array.isArray(bookingsResponse) ? bookingsResponse : []);
                statsData.totalBookings = bookingsList.length;

                setStats(statsData);
                const { vouchers: recent, forex, reviews, upcoming } = statsResponse.data?.recentActivity || {};
                setRecentVouchers(vouchersList.length > 0 ? vouchersList : (recent || []));
                setUpcomingVouchers(upcoming || []);
                setRecentForex(forex || []);
                setRecentReviews(reviews || []);
                
                const rawProfile = profileResponse?.data || profileResponse || {};
                const profileObj = rawProfile?.agent || rawProfile;
                const statsAgent = statsResponse?.data?.agent || {};
                const combinedAgent = {
                    ...profileObj,
                    ...statsAgent,
                    address: profileObj.address || statsAgent.address || '',
                    pincode: profileObj.pincode || statsAgent.pincode || '',
                    city: profileObj.city || statsAgent.city || '',
                    company_name: profileObj.company_name || statsAgent.company_name || profileObj.business_name || '',
                    logo: profileObj.logo || statsAgent.logo || '',
                    profiling_answers: (profileObj.profiling_answers && profileObj.profiling_answers.length > 0)
                        ? profileObj.profiling_answers
                        : (statsAgent.profiling_answers || []),
                    has_created_voucher: Boolean(statsAgent.has_created_voucher || profileObj.has_created_voucher),
                    has_shared_app: Boolean(statsAgent.has_shared_app || profileObj.has_shared_app),
                    has_traveller_downloaded: Boolean(statsAgent.has_traveller_downloaded || profileObj.has_traveller_downloaded),
                    has_traveller_on_tour: Boolean(statsAgent.has_traveller_on_tour || profileObj.has_traveller_on_tour)
                };
                setAgentProfile(combinedAgent);

                // ROBUST FALLBACK: Cache demo status so it survives server downtime on next reload
                if (statsResponse.data.agent?.is_demo_account) {
                    localStorage.setItem('agent_is_demo', 'true');
                } else {
                    localStorage.removeItem('agent_is_demo');
                }

                if (typeof statsResponse.data.agentUpdatesEnabled !== 'undefined') {
                    setAgentUpdatesEnabled(statsResponse.data.agentUpdatesEnabled);
                }

                setError(null);
            }
        } catch (err) {
            console.error('Error in DashboardState:', err);
            // If fetching vouchers failed but stats succeeded (or vice versa), we might want to handle gracefully
            // For now, we set error as before
            setError('Failed to load dashboard data');

            // Allow partial loading if only vouchers failed? 
            // The user prioritized the count availability, so erroring is probably safer to alert dev, 
            // but for UX, maybe just fallback to statsResponse count if vouchers fail.
        } finally {
            setLoading(false);
        }
    }, [dashboardService]);

    const fetchLocations = useCallback(async () => {
        try {
            const response = await dashboardService.getLocations();
            const data = response.data.data || response.data || [];
            const sortedData = [...data].sort((a, b) => a.name.localeCompare(b.name));
            setAllLocations(sortedData);
            return sortedData;
        } catch (err) {
            console.error("Error fetching locations:", err);
            return [];
        }
    }, [dashboardService]);

    const abortControllerRef = useRef(null);

    const startVoucherAnalysis = async (docFiles, itineraryFiles, mergeWith = null) => {
        // Abort previous request if any
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;

        console.log("🚀 STARTING AI ANALYSIS - Files:", { docs: docFiles.length, itinerary: itineraryFiles.length, isMerging: !!mergeWith });

        setAnalysisState(prev => ({
            ...prev,
            isAnalyzing: true,
            progress: 0,
            result: null,
            error: null,
            isMinimized: false,
            files: [...docFiles, ...itineraryFiles],
            analysisType: mergeWith ? 'enrich' : 'initial'
        }));

        let currentLocations = allLocations;
        if (currentLocations.length === 0) {
            currentLocations = await fetchLocations();
        }

        const progressInterval = setInterval(() => {
            setAnalysisState(prev => {
                if (prev.progress >= 90) return prev;
                return { ...prev, progress: prev.progress + Math.random() * 5 };
            });
        }, 800);

        // TIMEOUT FALLBACK: 60 Seconds
        const timeoutId = setTimeout(() => {
            if (localStorage.getItem('agent_is_demo') === 'true' || agentProfile?.is_demo_account) {
                console.log("⏰ 60s TIMEOUT REACHED - FORCING DEMO SUCCESS");
                clearInterval(progressInterval);

                const randomIndex = Math.floor(Math.random() * dummyVouchers.length);
                const selectedDummy = dummyVouchers[randomIndex];
                const dummyResponse = { ...selectedDummy, booking_id: Math.random().toString(36).substring(2, 7) };
                let processedSummary = postProcessAiData(dummyResponse, currentLocations);

                if (mergeWith) {
                    processedSummary = mergeVoucherData(mergeWith, processedSummary);
                }

                setAnalysisState(prev => ({
                    ...prev,
                    isAnalyzing: false,
                    progress: 100,
                    result: processedSummary,
                }));
            }
        }, 60000); // 1 minute

        try {
            const formData = new FormData();
            docFiles.forEach((file) => formData.append("docFiles", file));
            itineraryFiles.forEach((file) => formData.append("itineraryFiles", file));
            
            const currentBookingId = localStorage.getItem('trrip_voucher_booking_id');
            if (currentBookingId) formData.append('booking_id', currentBookingId);

            const response = await dashboardService.createVoucherAI(formData, { signal: controller.signal });
            clearTimeout(timeoutId); // Clear timeout on success

            console.log("DEBUG: RAW AI RESPONSE FROM SERVER:", response.summary);
            
            // Sync Booking ID if server returned a new one
            if (response.booking_id && response.booking_id !== currentBookingId) {
                console.log("📍 Syncing Booking ID from Server:", response.booking_id);
                localStorage.setItem('trrip_voucher_booking_id', response.booking_id);
            }

            const parsedSummary = cleanAndParseSummary(response.summary);
            let processedSummary = postProcessAiData(parsedSummary, currentLocations);

            if (mergeWith) {
                processedSummary = mergeVoucherData(mergeWith, processedSummary);
            }

            const rawSourceDocs = response.source_documents || response.summary?.source_documents;
            if (rawSourceDocs && Array.isArray(rawSourceDocs) && rawSourceDocs.length > 0) {
                processedSummary.source_documents = rawSourceDocs;
            }

            if (response.voucher_id) {
                processedSummary._id = response.voucher_id;
            }
            if (response.booking_id) {
                processedSummary.booking_id = response.booking_id;
            }

            console.log("DEBUG: PROCESSED AI DATA:", processedSummary);

            clearInterval(progressInterval);
            setAnalysisState(prev => ({
                ...prev,
                isAnalyzing: false,
                progress: 100,
                result: processedSummary,
            }));
        } catch (err) {
            clearInterval(progressInterval);
            clearTimeout(timeoutId);

            if (err.name === 'AbortError' || err.message === 'canceled') {
                console.log("🛑 AI Analysis Request Cancelled");
                return;
            }

            console.error("❌ AI EXTRACTION ERROR:", err);

            const isDemo = agentProfile?.is_demo_account || localStorage.getItem('agent_is_demo') === 'true';

            if (isDemo) {
                console.log("🚀 DEMO MODE: TRIGGERING CLIENT-SIDE FALLBACK");
                setTimeout(() => {
                    try {
                        const randomIndex = Math.floor(Math.random() * dummyVouchers.length);
                        const selectedDummy = dummyVouchers[randomIndex];
                        const dummyResponse = { ...selectedDummy, booking_id: Math.random().toString(36).substring(2, 7) };

                        let processedSummary = postProcessAiData(dummyResponse, currentLocations);
                        if (mergeWith) {
                            processedSummary = mergeVoucherData(mergeWith, processedSummary);
                        }

                        setAnalysisState(prev => ({
                            ...prev,
                            isAnalyzing: false,
                            progress: 100,
                            result: processedSummary,
                        }));
                    } catch (fallbackErr) {
                        console.error("Fallback failed", fallbackErr);
                        setAnalysisState(prev => ({ ...prev, isAnalyzing: false, error: "Analysis failed." }));
                    }
                }, 2000);
            } else {
                const errorMessage = err.response?.data?.message || err.message || "Failed to analyze document.";
                toast.error(errorMessage);
                setAnalysisState(prev => ({
                    ...prev,
                    isAnalyzing: false,
                    error: errorMessage
                }));
            }
        } finally {
            if (abortControllerRef.current === controller) {
                abortControllerRef.current = null;
            }
        }
    };

    const setAnalysisMinimized = useCallback((minimized) => {
        setAnalysisState(prev => ({ ...prev, isMinimized: minimized }));
    }, []);

    const resetAnalysis = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
        setAnalysisState({
            isAnalyzing: false,
            progress: 0,
            result: null,
            isMinimized: false,
            error: null,
            files: []
        });
    }, []);

    const dismissWelcome = async () => {
        try {
            await dashboardService.dismissWelcome();
            setAgentProfile(prev => ({ ...prev, show_welcome_popup: false }));
        } catch (err) {
            console.error("Error dismissing welcome:", err);
        }
    };

    // Real-time Booking Updates Logic
    useEffect(() => {
        const user_id = localStorage.getItem('user_id');
        if (!user_id) return;

        // Initialize socket
        socketRef.current = io(SOCKET_SERVER_URL);

        socketRef.current.on('connect', () => {
            console.log('⚡ [Dashboard Socket] Connected to server');
            // Join agent-specific room
            socketRef.current.emit('join-room', `agent_home_${user_id}`);
        });

        socketRef.current.on('new-booking', (data) => {
            console.log('✅ New booking received on Dashboard:', data);
            fetchDashboardData(); // Refresh all stats and recent activity
            toast.success('New booking received!', {
                icon: '🎫',
                style: {
                    borderRadius: '16px',
                    background: '#111827',
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: 'bold'
                }
            });
        });

        return () => {
            if (socketRef.current) {
                socketRef.current.disconnect();
            }
        };
    }, [fetchDashboardData]);

    return (
        <DashboardContext.Provider
            value={{
                stats,
                recentVouchers,
                upcomingVouchers,
                recentForex,
                recentReviews,
                agentProfile,
                loading,
                error,
                fetchDashboardData,
                analysisState,
                startVoucherAnalysis,
                setAnalysisMinimized,
                resetAnalysis,
                activeTab,
                setActiveTab,
                dismissWelcome,
                agentUpdatesEnabled
            }}
        >
            {props.children}
        </DashboardContext.Provider>
    );
};

export default DashboardState;
